/**
 * Module: copilot/agents
 * Responsibility: The agents a merchant can @-mention in the Home composer,
 *   and the pure text helpers behind the mention picker.
 * Routing contract (copilot backend, routing/agentRoute.ts): a conversation
 *   picks its agent once, on its first message. A mention travels as the
 *   structured `agent` field on POST /copilot/chat; the server never parses
 *   mentions out of the message text, so the token is removed before sending.
 */

export type AgentKind = 'copilot' | 'cro'

/** Where the merchant typed. Only Home conversations can reach the CRO agent. */
export type ChatSurface = 'home' | 'widget'

/** Per-send routing hints for POST /copilot/chat. All optional. */
export interface ChatRouting {
  readonly surface?: ChatSurface
  readonly agent?: AgentKind
  /** For a mention of the other agent mid-thread: the conversation this one continues. */
  readonly handoffFrom?: string
}

const AGENT_NAMES: Readonly<Record<AgentKind, string>> = {
  copilot: 'Flo-AI',
  cro: 'Converto'
}

/** The line shown when an @-mention moves the merchant into a new conversation. */
export function handoffNotice(agent: AgentKind): string {
  return `Continuing with ${AGENT_NAMES[agent]} in a new conversation.`
}

export interface MentionAgent {
  readonly id: AgentKind
  readonly label: string
  readonly description: string
}

/** An `@query` token ending at the caret: `start` is the index of the `@`. */
export interface MentionQuery {
  readonly start: number
  readonly end: number
  readonly query: string
}

const MENTION_TOKEN = /(^|\s)@([\w-]*)$/

/**
 * The `@query` the caret is in, or null. Only a token at the start of the text
 * or after whitespace counts, so an email address never opens the picker.
 */
export function findMentionQuery(text: string, caret: number): MentionQuery | null {
  const match = MENTION_TOKEN.exec(text.slice(0, caret))
  if (!match) return null

  const query = match[2]

  return { start: caret - query.length - 1, end: caret, query }
}

export function filterMentionAgents(agents: readonly MentionAgent[], query: string): readonly MentionAgent[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return agents

  return agents.filter(
    agent =>
      agent.id.startsWith(needle) ||
      agent.label
        .toLowerCase()
        .split(/\s+/)
        .some(word => word.startsWith(needle))
  )
}

/** The text with the typed `@query` (and one following space) removed. */
export function removeMentionToken(text: string, mention: MentionQuery): string {
  const after = text.slice(mention.end)

  return text.slice(0, mention.start) + (after.startsWith(' ') ? after.slice(1) : after)
}
